import bs58 from 'bs58';
import { PublicKey } from '@solana/web3.js';
import { ed25519 } from '@noble/curves/ed25519';

const OFFCHAIN_SIGNING_DOMAIN = Uint8Array.from([
  0xff,
  ...new TextEncoder().encode('solana offchain'),
]);

const SYSTEM_PROGRAM_DOMAIN = new Uint8Array(32);

function buildLegacyOffchainPayload(messageBytes: Uint8Array): Uint8Array {
  const out = new Uint8Array(16 + 2 + messageBytes.length);
  out.set(OFFCHAIN_SIGNING_DOMAIN, 0);
  new DataView(out.buffer, out.byteOffset + 16, 2).setUint16(0, messageBytes.length, true);
  out.set(messageBytes, 18);
  return out;
}

function buildOffchainV1Payload(messageBytes: Uint8Array, signerPubkey: Uint8Array): Uint8Array {
  const out = new Uint8Array(16 + 1 + 1 + 32 + messageBytes.length);
  let offset = 0;
  out.set(OFFCHAIN_SIGNING_DOMAIN, offset);
  offset += 16;
  out[offset++] = 1;
  out[offset++] = 1;
  out.set(signerPubkey, offset);
  offset += 32;
  out.set(messageBytes, offset);
  return out;
}

function buildOffchainV1WithLengthPayload(
  messageBytes: Uint8Array,
  signerPubkey: Uint8Array
): Uint8Array {
  const out = new Uint8Array(16 + 1 + 1 + 32 + 2 + messageBytes.length);
  let offset = 0;
  out.set(OFFCHAIN_SIGNING_DOMAIN, offset);
  offset += 16;
  out[offset++] = 1;
  out[offset++] = 1;
  out.set(signerPubkey, offset);
  offset += 32;
  new DataView(out.buffer, out.byteOffset + offset, 2).setUint16(0, messageBytes.length, true);
  offset += 2;
  out.set(messageBytes, offset);
  return out;
}

function buildOffchainV0Payload(params: {
  messageBytes: Uint8Array;
  signerPubkey: Uint8Array;
  applicationDomain: Uint8Array;
  messageFormat: 0 | 1 | 2;
}): Uint8Array {
  const { messageBytes, signerPubkey, applicationDomain, messageFormat } = params;
  const out = new Uint8Array(16 + 1 + 32 + 1 + 1 + 32 + 2 + messageBytes.length);
  let offset = 0;
  out.set(OFFCHAIN_SIGNING_DOMAIN, offset);
  offset += 16;
  out[offset++] = 0;
  out.set(applicationDomain, offset);
  offset += 32;
  out[offset++] = messageFormat;
  out[offset++] = 1;
  out.set(signerPubkey, offset);
  offset += 32;
  new DataView(out.buffer, out.byteOffset + offset, 2).setUint16(0, messageBytes.length, true);
  offset += 2;
  out.set(messageBytes, offset);
  return out;
}

function verifyDetached(
  payload: Uint8Array,
  signature: Uint8Array,
  publicKey: Uint8Array
): boolean {
  if (signature.length !== 64) return false;
  try {
    return ed25519.verify(signature, payload, publicKey);
  } catch {
    return false;
  }
}

function buildSignedPayloadCandidates(walletAddress: string, message: string): Uint8Array[] {
  const messageBytes = new TextEncoder().encode(message);
  const signerPubkey = new PublicKey(walletAddress).toBytes();
  const candidates: Uint8Array[] = [
    messageBytes,
    buildLegacyOffchainPayload(messageBytes),
    buildOffchainV1Payload(messageBytes, signerPubkey),
    buildOffchainV1WithLengthPayload(messageBytes, signerPubkey),
  ];

  for (const applicationDomain of [SYSTEM_PROGRAM_DOMAIN, signerPubkey]) {
    for (const messageFormat of [0, 1, 2] as const) {
      candidates.push(
        buildOffchainV0Payload({
          messageBytes,
          signerPubkey,
          applicationDomain,
          messageFormat,
        })
      );
    }
  }

  return candidates;
}

function buildSignedMessageCandidates(signedMessage: Uint8Array): Uint8Array[] {
  const candidates = [signedMessage];
  if (signedMessage.length > 65) {
    const signatureCount = signedMessage[0];
    if (signatureCount >= 1 && signatureCount <= 8) {
      const payloadStart = 1 + signatureCount * 64;
      if (payloadStart < signedMessage.length) {
        candidates.push(signedMessage.slice(payloadStart));
      }
    }
  }
  return candidates;
}

export function verifyOwnershipProof(params: {
  walletAddress: string;
  message: string;
  signature: Uint8Array;
  signedMessage?: Uint8Array;
}): boolean {
  try {
    const publicKey = new PublicKey(params.walletAddress).toBytes();
    const signature = params.signature.length === 64 ? params.signature : null;
    if (!signature) return false;

    if (params.signedMessage) {
      for (const payload of buildSignedMessageCandidates(params.signedMessage)) {
        if (verifyDetached(payload, signature, publicKey)) return true;
      }
    }

    for (const payload of buildSignedPayloadCandidates(params.walletAddress, params.message)) {
      if (verifyDetached(payload, signature, publicKey)) return true;
    }

    return false;
  } catch {
    return false;
  }
}

export function encodeSignatureForApi(signature: Uint8Array): {
  signature: number[];
  signature_base58: string;
} {
  return {
    signature: Array.from(signature),
    signature_base58: bs58.encode(signature),
  };
}
